import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UIRouter } from '@uirouter/angular';
import { ColDef, CsvExportParams, GridApi, GridReadyEvent, IRowNode, GetRowIdParams } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AgGridColumns, AgGridOptionsBuilder } from '../../../Compliance/AgGrid';
import {
    AgGridMultiSelectedCellRenderer,
    AgGridMultiSelectedHeaderRenderer,
    AgGridMultiSelectRendererParams,
    AgGridMultiSelectTracker,
} from '../../../Compliance/AgGrid/MultiSelectTracker';
import { AgGridExportOptions } from '../../../Compliance/AgGrid/ToolPanel/models';
import { ActionViewCategoryTypes } from '../../../constants.new';
import { AdvancedSearchColumnBuilder } from '../../../Search/Advanced/Advanced-Search-List/advancedSearchColumnBuilder.service';
import { AdvancedSearchPersistenceService } from '../../../Search/Advanced/advancedSearchPersistence.service';
import { SmartSearchService } from '../../../Search/Advanced/smartSearch.service';
import { ActionViewPersistenceService } from '../Action.View.Persistence.Service.upgrade';
import { ActionViewContextMenuAction, ActionViewContextMenuService } from '../actionViewContextMenu.service';
import {
    ACTION_VIEW_SEARCH_TIMEOUT_MESSAGE,
    ACTION_VIEW_SEARCH_TIMEOUT_TITLE,
    isActionViewSearchTimeout,
    normalizeActionViewRequest,
    parseActionViewSavedCriteria,
} from '../actionView.adapters';
import { ActionViewSearchRequest } from '../actionView.models';
import { ActionViewApiService } from '../actionView.service';
import { ActionViewCriteriaService } from '../actionViewCriteria.service';
import { ActionViewStateService } from '../actionViewState.service';

import * as _ from 'lodash';
import moment from 'moment';

@Component({
    selector: 'action-view-detailed',
    templateUrl: './actionDetailed.component.html',
    styleUrls: ['./actionDetailed.component.scss'],
    standalone: false,
})
export class ActionDetailedComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _actionViewService: ActionViewApiService,
        private readonly _actionViewContextMenuService: ActionViewContextMenuService,
        private readonly _actionViewPersistenceService: ActionViewPersistenceService,
        private readonly _advancedSearchPersistenceService: AdvancedSearchPersistenceService,
        private readonly _uiRouter: UIRouter,
        private readonly _smartSearchService: SmartSearchService,
        private readonly _columnBuilder: AdvancedSearchColumnBuilder,
        private readonly _toastr: ToastrService,
        private readonly _actionViewStateService: ActionViewStateService,
        private readonly _actionViewCriteriaService: ActionViewCriteriaService,
    ) {}

    @Input() filterToExecute;
    @Input() drilldownMode;
    @Input() drilldownFilter;
    @Input() results;
    @Input() actionView;

    @Output() backToOverview: EventEmitter<boolean> = new EventEmitter<boolean>();

    isSearchTimedOut: boolean = false;
    timeoutMessage: string = ACTION_VIEW_SEARCH_TIMEOUT_MESSAGE;
    gridId: string = '64f50d82-7571-4cdc-a705-f5100b054f16';
    gridTracker: AgGridMultiSelectTracker;
    gridOptions = new AgGridOptionsBuilder({
        onFilterChanged: () => {
            this._actionViewPersistenceService.filter = this.gridApi.getFilterModel();
            this.gridTracker.onGridFilterChanged(false);
        },
        onSortChanged: () => {
            this._actionViewPersistenceService.sort = this.gridApi
                .getColumnState()
                .filter((x) => x.sort)
                .map((x) => ({
                    colId: x.colId,
                    sort: x.sort,
                }));
            this.gridTracker.onGridSortChanged();
        },
        getRowId: (params: GetRowIdParams) => params.data.n10908,
        rowClassRules: {
            clickable: () => false,
            'ag-row-selected': (params) => params.data && this.gridTracker.isRowSelected(params.data.n10908),
            'ag-row-stale': (params) => params.data && params.data.stale,
        },
        getRowStyle: (params) => {
            if (params.data.error) {
                return { color: 'red' };
            } else if (params.data.warning) {
                return { color: '#ffa000', 'font-weight': 'bold' };
            } else if (params.data.stale) {
                return { 'font-style': 'italic', color: 'var(--ace-neutral-300)' };
            }
        },
        suppressScrollOnNewData: true,
        onRowDataUpdated: () => {
            if (this._advancedSearchPersistenceService.scrollLocationIndex) {
                this.gridApi.ensureIndexVisible(this._advancedSearchPersistenceService.scrollLocationIndex, 'top');
                this._advancedSearchPersistenceService.scrollLocationIndex = null;
            }
        },
    })
        .withSort()
        .withColumnResize()
        .withMultipleColumnSort()
        .withLoadingOverlay()
        .withTextSelection()
        .build();

    exportOptions: AgGridExportOptions = {
        onExportClick: (): void => {
            this.exportToExcel();
        },
        disabled: false,
        canCancel: true,
    };

    contextMenuItems: ActionViewContextMenuAction[] | null;
    contextMenuLoading = false;
    contextMenuOpen = false;
    showContextMenu = false;

    loading = false;
    loadingText = null;
    isDestroyed = false;
    showErrorsOnly = {
        value: false,
    };
    showValidRowsOnly = {
        value: false,
    };
    originalData = null;
    columnsExpanded: boolean;
    excelDownloading: boolean;
    gridApi: GridApi;

    private _destroy$: Subject<void> = new Subject();

    get contextMenuDisabled(): boolean {
        return !this.gridTracker?.hasSelectedRows() || this.contextMenuLoading;
    }

    get contextMenuTooltip(): string {
        if (!this.gridTracker?.hasSelectedRows()) {
            return 'Please select at least one row to perform an action';
        } else if (this.contextMenuLoading) {
            return 'Loading actions...';
        } else if (!this.contextMenuItems?.length) {
            return 'Load selected actions';
        } else {
            return null;
        }
    }

    async ngOnInit(): Promise<void> {
        this._actionViewContextMenuService.loading$.pipe(takeUntil(this._destroy$)).subscribe((loading) => {
            this.loading = !!loading;
            this.loadingText = loading;

            if (this.loading) {
                this.contextMenuOpen = false;
                this.contextMenuItems = null;
            }
        });
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
        this._actionViewContextMenuService.clearErrors();
        this.isDestroyed = true;
    }

    async onAgGridReady(event: GridReadyEvent): Promise<void> {
        this.gridApi = event.api;

        this.gridTracker = new AgGridMultiSelectTracker(this.gridOptions, this.gridApi, this._getGridRowIds.bind(this));

        this.gridTracker.selectedRows$.pipe(takeUntil(this._destroy$)).subscribe(() => {
            this.contextMenuItems = null;
            this.showContextMenu = this.gridTracker.hasSelectedRows();
        });

        let finalFilter = structuredClone(this.filterToExecute);

        if (this.drilldownMode && this.drilldownFilter) {
            finalFilter = structuredClone(this.drilldownFilter);
        } else if (this._actionViewPersistenceService.drilldownFilter) {
            finalFilter = structuredClone(this._actionViewPersistenceService.drilldownFilter);
        } else if (this._actionViewPersistenceService.resultsChanged) {
            finalFilter = structuredClone(this._actionViewPersistenceService.masterFilter);
            this.filterToExecute = structuredClone(finalFilter);
            await this.executeDetailedFilter(finalFilter);
        }

        //if we have no cached results and we have a filter
        if (!this.results && this.filterToExecute !== undefined) {
            await this.executeDetailedFilter(finalFilter);
        }
        // if we have no saved cached results
        else if (!this._actionViewPersistenceService.detailResults) {
            const stateParams = this._uiRouter.globals.params;
            const actionViewId = parseInt(stateParams.actionViewId);
            const isSystemView = parseInt(stateParams.actionViewType) === ActionViewCategoryTypes.SYSTEM;

            if (isSystemView || !this._actionViewPersistenceService.resultsChanged) {
                const savedCriteria = await this._loadSavedDetailedCriteria(actionViewId, isSystemView);
                await this.executeDetailedFilter(savedCriteria);
            }
        } else if (
            this._actionViewPersistenceService.detailResults &&
            !this._actionViewPersistenceService.resultsChanged
        ) {
            this.results = this._actionViewPersistenceService.detailResults;
        } else if (!this.filterToExecute && finalFilter) {
            this.filterToExecute = structuredClone(finalFilter);
        }

        if (this.results) {
            this._buildTable(this.results.dataTable);
        }

        this._setRowData();
    }

    setPersistenceServiceWithResult(result): void {
        this._actionViewPersistenceService.detailResults = structuredClone(result);
        if (this._actionViewStateService.snapshot) {
            this._actionViewStateService.storeDetail({
                results: structuredClone(result),
                grid: null,
            });
        }
        this.setPersistenceService();
    }

    setPersistenceService(): void {
        if (!this._actionViewPersistenceService.masterFilter) {
            this._actionViewPersistenceService.masterFilter = structuredClone(this.filterToExecute) || {};
        }

        // If specific user or teams are blank in the filters, include an empty object so the filter screens
        // restore with an editable blank row instead of null when the user returns.
        this._actionViewPersistenceService.masterFilter.ShowUserTeams = this._actionViewPersistenceService.masterFilter
            .ShowUserTeams || [{}];
        this._actionViewPersistenceService.masterFilter.ShowTeams = this._actionViewPersistenceService.masterFilter
            .ShowTeams || [{}];

        if (this.drilldownMode) {
            this._actionViewPersistenceService.drilldownFilter = structuredClone(this.drilldownFilter);
        }
        const stateParams = this._uiRouter.globals.params;
        this._actionViewPersistenceService.routeParams = structuredClone(stateParams);
        this._actionViewPersistenceService.actionViewType = stateParams.actionViewType;
        this._actionViewPersistenceService.drilldownMode = structuredClone(this.drilldownMode);
        this._actionViewPersistenceService.actionViewName = this.actionView.actionViewName;

        if (this._uiRouter.globals.current.name === 'actionview' && this._actionViewStateService.snapshot) {
            this._actionViewStateService.prepareExternalNavigation({
                state: 'actionview',
                params: {
                    actionViewId: stateParams.actionViewId ? Number(stateParams.actionViewId) : undefined,
                    actionViewType:
                        stateParams.actionViewType === null || stateParams.actionViewType === undefined
                            ? undefined
                            : Number(stateParams.actionViewType),
                    displayType:
                        stateParams.displayType === null || stateParams.displayType === undefined
                            ? undefined
                            : Number(stateParams.displayType),
                },
            });
        }
    }

    async executeDetailedFilter(filter): Promise<void> {
        this._actionViewPersistenceService.selectionData = [];

        this.filterToExecute = filter;
        this.isSearchTimedOut = false;

        try {
            const actionViewId = Number(this._uiRouter.globals.params.actionViewId) || null;
            const result = await this._actionViewService.executeActionView(
                normalizeActionViewRequest(filter),
                actionViewId,
            );

            if (!result) {
                return;
            }

            if (result?.dataTable?.length >= 50000) {
                this._toastr.warning(
                    '50,000 records are shown; if you want to see all results please use the "Excel" export option.',
                    'Action View',
                );
            }

            this.results = result;

            // please do not remove this console output,Grant is using it
            //
            console.info(['ActionView Detailed Search results', result]);
            console.info(['ActionView Detailed Search result sql', result.sql]);

            this.setPersistenceServiceWithResult(result);
        } catch (err) {
            {
                console.log(['ActionView search - Failure', err]);
                // error handler
                if (err.status === 0) {
                    // $http timeout
                    console.log('ActionView search - Failure - $http timeout');
                } else {
                    // response error status from server
                    console.log('ActionView search - Failure ');
                    if (isActionViewSearchTimeout(err)) {
                        this.isSearchTimedOut = true;
                        this._toastr.error(ACTION_VIEW_SEARCH_TIMEOUT_MESSAGE, ACTION_VIEW_SEARCH_TIMEOUT_TITLE);
                    } else {
                        this._toastr.error('', 'Action View search failed');
                    }
                    //warningService.warning("", "Action View search has been cancelled", 6000);
                    this.results = {};
                    this.originalData = null;
                    //showFilter()
                }
            }
        }
    }

    async getTableData(): Promise<void> {
        this.gridApi.setGridOption('loading', true);
        this.showErrorsOnly.value = false;
        this.showValidRowsOnly.value = false;
        this._actionViewContextMenuService.clearErrors();

        this.gridTracker.clear();

        let resetFilter;
        // Figure out which filter we need. The three states are basically
        // 1) use the persistence service. This is when we return using the nub and takes precedence
        // 2) use the drilldown filter. This means we have drilled down from the overview page and need to use that
        // 3) lastly, use the filter on the page. This is most common. This is a normally executed filter.
        if (this.drilldownMode && !_.isEmpty(this.drilldownFilter)) {
            resetFilter = this.drilldownFilter;
        } else if (this._actionViewPersistenceService.masterFilter && !this.filterToExecute) {
            resetFilter = this._actionViewPersistenceService.masterFilter;
        } else if (this.filterToExecute) {
            resetFilter = this.filterToExecute;
        }

        if (resetFilter) {
            this.results = null;
            this.originalData = null;
            await this.executeDetailedFilter(resetFilter);

            if (this.results) {
                this._buildTable(this.results.dataTable);
            }

            this._setRowData();
        }
    }

    async getMenuOptions(): Promise<void> {
        if (this.contextMenuOpen) {
            this.contextMenuOpen = false;
            return;
        }

        if (this.contextMenuItems?.length) {
            this.contextMenuOpen = true;
            return;
        }

        this.contextMenuLoading = true;

        this.setPersistenceService();
        this.contextMenuItems = await this._actionViewContextMenuService.getContextMenu(
            this.gridApi,
            this.gridTracker,
            this.results.searchTimestamp,
        );

        this.contextMenuOpen = true;
        this.contextMenuLoading = false;
    }

    // remove CSV export per Grant comment regarding WK-4228 (already removed on the UI)
    exportToCsv(): void {
        const params = {
            fileName: `action-view - ${moment().format()}`,
        } as CsvExportParams;
        this.gridApi.exportDataAsCsv(params);
    }

    async exportToExcel(): Promise<void> {
        this.excelDownloading = true;
        const filterToExecute = this.drilldownMode
            ? structuredClone(this.drilldownFilter)
            : structuredClone(this.filterToExecute);

        if (!this.actionView.actionViewName || this.actionView.actionViewName.trim() == '') {
            filterToExecute.searchName = 'Action View Results';
        } else {
            filterToExecute.searchName = this.actionView.actionViewName.replace(/[^a-z0-9_ \-]/gi, '');
        }

        await this._actionViewService.startExcelExport(normalizeActionViewRequest(filterToExecute));
        this.excelDownloading = false;
    }

    resizeColumns(value: boolean): void {
        this.columnsExpanded = value;
    }

    hasErrors(): boolean {
        return this._actionViewContextMenuService.hasErrors();
    }

    filterResults(): void {
        if (!this.originalData) {
            this.originalData = structuredClone(this.results.dataTable);
        }

        if (this.showErrorsOnly.value || this.showValidRowsOnly.value) {
            this.results.dataTable = this.originalData.filter(
                (x) =>
                    (this.showErrorsOnly.value && (x.error || x.warning)) ||
                    (this.showValidRowsOnly.value && !x.error && !x.warning),
            );
        } else {
            this.results.dataTable = this.originalData;
        }

        this.gridApi.setGridOption('rowData', this.results.dataTable);
    }

    private async _loadSavedDetailedCriteria(
        actionViewId: number,
        isSystemView: boolean,
    ): Promise<ActionViewSearchRequest> {
        const response = isSystemView
            ? await this._actionViewService.getSystemActionViewById(actionViewId)
            : await this._actionViewService.getCustomActionViewById(actionViewId);
        const savedCriteria = parseActionViewSavedCriteria(response.criteria);
        const criteriaData = await this._actionViewCriteriaService.load();
        const hydratedCriteria = this._actionViewCriteriaService.hydrateSavedOutputColumns(
            criteriaData,
            savedCriteria.masterFilter,
        );

        return { ...hydratedCriteria, detailedResults: true };
    }

    private _setRowData(): void {
        if (!(this.gridApi && this.results)) {
            return;
        }

        this.gridApi.setGridOption('rowData', this.results.dataTable);
        // Force row style/class (e.g. stale italic/grey) to recompute against the fresh row data;
        // without this, getRowStyle/rowClassRules don't reliably re-evaluate on the first refresh. See #586250.
        this.gridApi.redrawRows();
        if (this._advancedSearchPersistenceService.columnsExpanded) {
            this.gridApi.sizeColumnsToFit();
        } else {
            this.gridApi.autoSizeAllColumns();
        }
        this._setAgGridValues();
        this.gridApi.setGridOption('loading', false);
    }

    private _setAgGridValues(): void {
        if (this._actionViewPersistenceService.sort) {
            this.gridApi.applyColumnState(this._actionViewPersistenceService.sort);
        }
        if (this._actionViewPersistenceService.filter) {
            this.gridApi.setFilterModel(this._actionViewPersistenceService.filter);
        }
    }

    private _buildTable(tableData: Record<string, unknown>[]): void {
        if (!this.gridApi) {
            return;
        }

        // Important - For whatever reason, ag-grid will maintain the previous search column order if not reset
        this.gridApi.setGridOption('columnDefs', []);

        const search = this._advancedSearchPersistenceService.search;
        const columnIds = tableData && tableData.length ? Object.keys(tableData[0]) : [];

        // Reorder columnIds to match outputColumn order
        if (search && search.searchCriteria && search.searchCriteria.outputColumns) {
            const outputIds = search.searchCriteria.outputColumns.map((x) => x.columnId);
            columnIds.sort((a, b) => {
                return outputIds.indexOf(+a.slice(1)) - outputIds.indexOf(+b.slice(1));
            });
        }

        const multiselectColDef = {
            colId: 'grid-column-multiselect',
            headerName: '',
            field: 'n10908',
            width: AgGridColumns.selectionColumnWidth,
            maxWidth: AgGridColumns.selectionColumnWidth,
            suppressSizeToFit: true,
            suppressAutoSize: true,
            suppressColumnsToolPanel: true,
            editable: false,
            pinned: 'left',
            lockPinned: true,
            lockVisible: true,
            lockPosition: true,
            suppressMovable: true,
            headerComponent: AgGridMultiSelectedHeaderRenderer,
            headerComponentParams: { tracker: this.gridTracker } as AgGridMultiSelectRendererParams,
            cellRenderer: AgGridMultiSelectedCellRenderer,
            cellRendererParams: { tracker: this.gridTracker } as AgGridMultiSelectRendererParams,
        } as ColDef;

        const columnProperties = columnIds.reduce<ColDef[]>((acc, x) => {
            const fieldId = +x.slice(1);
            if (isNaN(fieldId)) {
                return acc;
            }

            const field = this._smartSearchService.getFieldById(fieldId);
            if (!field) {
                return acc;
            }

            const baseColumn = this._columnBuilder.getColumnDefinition(x, field, this.gridApi, this.gridTracker, 'av');

            acc.push(baseColumn);
            return acc;
        }, []);

        const columns = [multiselectColDef, ...columnProperties];

        this.gridApi.setGridOption('columnDefs', columns);
        this._advancedSearchPersistenceService.applyGridState(this.gridApi, this.gridTracker);

        this.gridApi.setGridOption('rowData', tableData);
    }

    private _clearTable(): void {
        if (!this.gridApi) {
            return;
        }

        this.gridTracker.clear();
        this.gridApi.setGridOption('rowData', []);
    }

    private async _getGridRowIds(skip: number, take: number): Promise<Compliance.QueryResultModel<number>> {
        const model = { rowsToDisplay: [] };
        this.gridApi.forEachNodeAfterFilterAndSort((x) => model.rowsToDisplay.push(x));
        const rows: IRowNode[] = model.rowsToDisplay.slice(skip, take + 1);
        return {
            data: rows.map((x) => {
                return x.data.n10908;
            }),
        } as Compliance.QueryResultModel<number>;
    }
}
